import { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  showToast,
  TextInput,
  Button,
  HeadingText,
  Checkbox,
  CheckboxGroup,
} from '@spotoninc/nexus-react';
import {
  FormControl,
  FormLabel,
  VStack,
  HStack,
  Stack,
  FormErrorMessage,
  Box,
  Text,
} from '@chakra-ui/react';
import { fetchWithAuth } from '../../utils/apiHelpers';

const AddOrEditRoleModal = ({
  isOpen,
  onClose,
  onSubmit,
  role,
  screens = [],
  mode = 'add', // 'add' or 'edit'
}) => {
  const possiblePages = [
    'dashboard',
    'budget',
    'task',
    'benchmark',
    'cost',
    'financialAnalysis',
    'locationAnalysis',
    'dateAnalysis',
  ];
  // Define which pages should be nested under Financial Insights (financialAnalysis)
  const nestedPages = ['locationAnalysis', 'dateAnalysis'];

  // Filter pages and organize them
  const pages = screens.filter(screen => possiblePages.includes(screen.key));
  const settings = screens.filter(
    screen => !possiblePages.includes(screen.key)
  );

  // Separate financial insights related pages
  const financialAnalysisPage = pages.find(
    screen => screen.key === 'financialAnalysis'
  );
  const nestedPagesData = pages.filter(screen =>
    nestedPages.includes(screen.key)
  );
  const regularPages = pages.filter(
    screen =>
      !nestedPages.includes(screen.key) && screen.key !== 'financialAnalysis'
  );
  const [formData, setFormData] = useState({
    roleName: '',
    permissions: [],
  });
  const [errors, setErrors] = useState({
    roleName: '',
    permissions: '',
  });
  const [loading, setLoading] = useState(false);
  const pageIds = pages.map(screen => String(screen.id));
  const settingsIds = settings.map(screen => String(screen.id));

  const allPagesChecked =
    pageIds.length > 0 &&
    pageIds.every(id => formData.permissions.includes(id));
  const isPagesIndeterminate =
    pageIds.some(id => formData.permissions.includes(id)) && !allPagesChecked;

  const allSettingsChecked =
    settingsIds.length > 0 &&
    settingsIds.every(id => formData.permissions.includes(id));
  const isSettingsIndeterminate =
    settingsIds.some(id => formData.permissions.includes(id)) &&
    !allSettingsChecked;

  const handleSelectAll = (ids, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: checked
        ? Array.from(new Set([...prev.permissions, ...ids]))
        : prev.permissions.filter(id => !ids.includes(id)),
    }));
  };

  // Function to fetch detailed role data from the API
  const fetchRoleData = async roleId => {
    if (!roleId) return;

    setLoading(true);
    try {
      const response = await fetchWithAuth(
        '/api/get_role_data/' + roleId + '/',
        {
          method: 'GET',
        }
      );

      const data = await response.json();

      if (data.code === undefined) {
        // Extract permissions from the data
        let permissions = [];
        if (Array.isArray(data)) {
          permissions = data.map(String);
        } else if (typeof data === 'string') {
          permissions = [data];
        } else if (data && Array.isArray(data.screens)) {
          permissions = data.screens.map(String);
        }

        setFormData({
          roleName: role?.description || '',
          permissions: permissions,
        });
      } else {
        window.open('/login', '_self');
        showToast({
          priority: 'error',
          content: 'Session Expired!',
        });
      }
    } catch (err) {
      console.error('Error fetching role details:', err);
      showToast({
        priority: 'error',
        content: 'Failed to fetch role details',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Always reset the form data immediately when the modal opens or changes roles
    // This prevents showing previous role's data while loading new data
    if (isOpen) {
      // Clear permissions immediately to prevent showing old data
      setFormData(prev => ({
        ...prev,
        permissions: [],
      }));

      setErrors({ roleName: '', permissions: '' });

      if (mode === 'edit' && role?.role_id) {
        // If in edit mode and we have a role ID, fetch detailed role data
        fetchRoleData(role.role_id);
      } else {
        // If in add mode, reset form data completely
        setFormData({
          roleName: '',
          permissions: [],
        });
      }
    }
  }, [role, isOpen, mode]);

  // Function to reset all state
  const resetState = () => {
    setFormData({
      roleName: '',
      permissions: [],
    });
    setErrors({
      roleName: '',
      permissions: '',
    });
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    if (!formData.roleName.trim()) {
      newErrors.roleName = 'Role name is required';
      isValid = false;
    }
    if (!formData.permissions.length) {
      newErrors.permissions = 'Select at least one permission';
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  // Function to handle form submission (add or edit)
  const handleSubmit = async () => {
    // Validate the form
    if (!validateForm()) {
      showToast({
        priority: 'error',
        content: 'Please fix the errors in the form',
      });
      return;
    }

    setLoading(true);

    try {
      // Call the parent component's onSubmit
      await onSubmit(formData);

      // Prepare data to send to the API
      const data = new FormData();

      // Add roleId if in edit mode
      if (mode === 'edit' && role?.role_id) {
        data.append('roleId', role.role_id);
        data.append('type', 'edit');
      } else {
        data.append('type', 'add');
      }

      // Add role name
      data.append('roleName', formData.roleName);

      // Add permissions (screen_list)
      data.append('screen_list', formData.permissions);

      // Send the request to the API
      const response = await fetchWithAuth('/api/set_screen_role/', {
        method: 'POST',
        body: data,
      });

      const responseData = await response.json();

      if (responseData.code === undefined) {
        // Show appropriate success message
        const successMessage =
          mode === 'edit'
            ? 'Role updated successfully'
            : 'Role created successfully';

        showToast({
          priority: 'success',
          content: successMessage,
        });

        // Reset state after successful submission
        resetState();
        onClose();
      } else {
        window.open('/login', '_self');
        showToast({
          priority: 'error',
          content: 'Session Expired!',
        });
      }
    } catch (err) {
      console.error(`Error ${mode} role data:`, err);
      showToast({
        priority: 'error',
        content: `Failed to ${mode === 'edit' ? 'update' : 'create'} role`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        resetState();
        onClose();
      }}
      style={{ width: '800px' }}
      data-testid="add-or-edit-role-modal"
    >
      <ModalHeader>
        <Stack>
          <HeadingText>
            {mode === 'add' ? 'Define Role' : 'Edit Role'}
          </HeadingText>
        </Stack>
      </ModalHeader>
      <ModalBody>
        <VStack spacing={4} align="stretch">
          <FormControl isInvalid={!!errors.roleName} mb={4}>
            <FormLabel>Role Name</FormLabel>
            <TextInput
              value={formData.roleName}
              onChange={e => {
                setFormData({ ...formData, roleName: e.target.value });
              }}
              placeholder="Enter role name"
            />
            <FormErrorMessage>{errors.roleName}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors.permissions}>
            <HeadingText size="small">Define Permissions</HeadingText>
            <Box p={3} position="relative">
              {loading && mode === 'edit' && (
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  bg="rgba(255, 255, 255, 0.7)"
                  zIndex="1"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text>Loading permissions...</Text>
                </Box>
              )}
              <HStack align="start" spacing={8} flexWrap="wrap">
                {/* Pages Group */}
                <Box>
                  <CheckboxGroup
                    label="Pages"
                    checked={allPagesChecked}
                    indeterminate={isPagesIndeterminate}
                    onChange={e => handleSelectAll(pageIds, e.target.checked)}
                  >
                    {/* Regular pages (not nested) */}
                    {regularPages.map(screen => (
                      <Checkbox
                        key={screen.id}
                        label={screen.name}
                        checked={formData.permissions.includes(
                          String(screen.id)
                        )}
                        onChange={e => {
                          const screenId = String(screen.id);
                          const isChecked = e.target.checked;
                          setFormData(prev => ({
                            ...prev,
                            permissions: isChecked
                              ? [...prev.permissions, screenId]
                              : prev.permissions.filter(id => id !== screenId),
                          }));
                        }}
                      />
                    ))}

                    {/* Financial Insights with nested items */}
                    {financialAnalysisPage && (
                      <>
                        <Checkbox
                          key={financialAnalysisPage.id}
                          label={financialAnalysisPage.name}
                          checked={formData.permissions.includes(
                            String(financialAnalysisPage.id)
                          )}
                          onChange={e => {
                            const screenId = String(financialAnalysisPage.id);
                            const isChecked = e.target.checked;

                            // Get IDs of nested pages
                            const nestedIds = nestedPagesData.map(item =>
                              String(item.id)
                            );

                            setFormData(prev => {
                              let newPermissions = [...prev.permissions];

                              if (isChecked) {
                                // Add Financial Insights
                                if (!newPermissions.includes(screenId)) {
                                  newPermissions.push(screenId);
                                }

                                // Add all nested items if parent is checked
                                nestedIds.forEach(id => {
                                  if (!newPermissions.includes(id)) {
                                    newPermissions.push(id);
                                  }
                                });
                              } else {
                                // Remove Financial Insights
                                newPermissions = newPermissions.filter(
                                  id => id !== screenId
                                );

                                // Remove all nested items if parent is unchecked
                                newPermissions = newPermissions.filter(
                                  id => !nestedIds.includes(id)
                                );
                              }

                              return {
                                ...prev,
                                permissions: newPermissions,
                              };
                            });
                          }}
                        />

                        {/* Nested items under Financial Insights */}
                        <Box pl={6} mt={1} mb={2} position="relative">
                          {/* Connector line */}
                          <Box
                            position="absolute"
                            left="0"
                            top="-10px"
                            width="10px"
                            height="10px"
                            borderColor="gray.200"
                          />
                          {nestedPagesData.map(screen => (
                            <Checkbox
                              key={screen.id}
                              label={screen.name}
                              checked={formData.permissions.includes(
                                String(screen.id)
                              )}
                              onChange={e => {
                                const screenId = String(screen.id);
                                const isChecked = e.target.checked;
                                const parentId = String(
                                  financialAnalysisPage.id
                                );

                                setFormData(prev => {
                                  let newPermissions = [...prev.permissions];

                                  if (isChecked) {
                                    // Add this item
                                    if (!newPermissions.includes(screenId)) {
                                      newPermissions.push(screenId);
                                    }

                                    // Also add parent if not already included
                                    if (!newPermissions.includes(parentId)) {
                                      newPermissions.push(parentId);
                                    }
                                  } else {
                                    // Remove this item
                                    newPermissions = newPermissions.filter(
                                      id => id !== screenId
                                    );

                                    // Check if any other nested items are still selected
                                    const anyNestedSelected = nestedPagesData
                                      .filter(
                                        item => String(item.id) !== screenId
                                      )
                                      .some(item =>
                                        newPermissions.includes(String(item.id))
                                      );

                                    // If no nested items are selected, also uncheck the parent
                                    if (!anyNestedSelected) {
                                      newPermissions = newPermissions.filter(
                                        id => id !== parentId
                                      );
                                    }
                                  }

                                  return {
                                    ...prev,
                                    permissions: newPermissions,
                                  };
                                });
                              }}
                            />
                          ))}
                        </Box>
                      </>
                    )}
                  </CheckboxGroup>
                </Box>
                {/* Settings Group */}
                <Box>
                  <CheckboxGroup
                    label="Settings"
                    checked={allSettingsChecked}
                    indeterminate={isSettingsIndeterminate}
                    onChange={e =>
                      handleSelectAll(settingsIds, e.target.checked)
                    }
                  >
                    {settings.map(screen => (
                      <Checkbox
                        key={screen.id}
                        label={screen.name}
                        checked={formData.permissions.includes(
                          String(screen.id)
                        )}
                        onChange={e => {
                          const screenId = String(screen.id);
                          const isChecked = e.target.checked;
                          setFormData(prev => ({
                            ...prev,
                            permissions: isChecked
                              ? [...prev.permissions, screenId]
                              : prev.permissions.filter(id => id !== screenId),
                          }));
                        }}
                      />
                    ))}
                  </CheckboxGroup>
                </Box>
              </HStack>
            </Box>
            <FormErrorMessage>{errors.permissions}</FormErrorMessage>
          </FormControl>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="tonal"
          mr={3}
          onClick={() => {
            // Reset state before closing
            resetState();
            onClose();
          }}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button colorScheme="blue" onClick={handleSubmit} isLoading={loading}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddOrEditRoleModal;
